import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import RondleidingForm from "../components/RondleidingForm";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Home.svg";
import WhatsAppButton from "../components/WhatsappButton";

const Home: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [openForm, setOpenForm] = React.useState<
    null |  "rondleiding"
  >(null);

  // Services data with routes and features
  const services = [
    {
      path: "/services",
      title: t("home.services.items.childcarePrograms.title"),
      icon: "🏠",
      features: t("home.services.items.childcarePrograms.features") || [],
    },
    {
      path: "/transportation-policy",
      title: t("home.services.items.transportPickup.title"),
      icon: "🚌",
      features: t("home.services.items.transportPickup.features") || [],
    },
    {
      path: "/nutrition",
      title: t("home.services.items.healthyMeals.title"),
      icon: "🍎",
      features: t("home.services.items.healthyMeals.features") || [],
    },
    {
      path: "/ipad-system",
      title: t("home.services.items.digitalTools.title"),
      icon: "📱",
      features: t("home.services.items.digitalTools.features") || [],
    },
    {
      path: "/daily-routine",
      title: t("home.services.items.dailyRoutines.title"),
      icon: "⏰",
      features: t("home.services.items.dailyRoutines.features") || [],
    },
    {
      path: "/emotional-safety",
      title: t("home.services.items.emotionalSafety.title"),
      icon: "❤️",
      features: t("home.services.items.emotionalSafety.features") || [],
    },
    {
      path: "/activities",
      title: t("home.services.items.mealtimesActivities.title"),
      icon: "🎨",
      features: t("home.services.items.mealtimesActivities.features") || [],
    },
    // {
    //   path: "/horizontal-groups",
    //   title: t("home.services.items.ageGroupStructure.title"),
    //   icon: "👶",
    //   features: t("home.services.items.ageGroupStructure.features") || [],
    // },
    {
      path: "/educational-staff",
      title: t("home.services.items.transitions.title"),
      icon: "🎓",
      features: t("home.services.items.transitions.features") || [],
    },
    {
      path: "/receiving-policy",
      title: t("home.services.items.settlingPolicy.title"),
      icon: "🏡",
      features: t("home.services.items.settlingPolicy.features") || [],
    },
    {
      path: "/parent-collaboration",
      title: t("home.services.items.parentCommunication.title"),
      icon: "👨‍👩‍👧‍👦",
      features: t("home.services.items.parentCommunication.features") || [],
    },
    {
      path: "/safety-hygiene",
      title: t("home.services.items.hygiene.title"),
      icon: "🧼",
      features: t("home.services.items.hygiene.features") || [],
    },
    // {
    //   path: "/opening-closing-checklist",
    //   title: t("home.services.items.openingClosing.title"),
    //   icon: "📅",
    //   features: t("home.services.items.openingClosing.features") || [],
    // },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        <main>
          {/* Hero Section */}
          <section className="relative py-20 px-4 sm:px-6 lg:px-8 hero-section-h" aria-label="Homepage hero section">
            <div className="absolute inset-0 pointer-events-none z-0">
              {/* Left SVG */}
              <img
                src={LeftSVG}
                alt="left decoration"
                className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
                loading="lazy"
              />
              {/* Right SVG */}
              <img
                src={RightSVG}
                alt="right decoration"
                className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[350px] w-full opacity-100"
                loading="lazy"
              />
            </div>

            {/* Main Content - Centered */}
            <div className="relative z-10 px-4 p-20">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-8 leading-tight text-center">
                  {t("home.welcome")}
                </h1>
                <p className="text-lg md:text-xl text-neutral-800 mb-10 max-w-4xl leading-relaxed text-center">
                  {t("home.subtitle")}
                </p>
                <div className="flex flex-col absolute left-0 sm:relative sm:justify-center sm:flex-row justify-center gap-4">
                  <button
                    className="px-8 py-3 bg-[#FBEACD] text-[#492A19] font-semibold rounded-full transition-all duration-300 hover:bg-[#492A19] hover:text-[#F9DCAC] border-2"
                    style={{ borderColor: "#492A19" }}
                    onClick={() => navigate("/register")}
                  >
                    {t("home.registerChild")}
                  </button>
                  <button
                    className="px-8 py-3 bg-[#FBEACD] text-[#492A19] font-semibold rounded-full transition-all duration-300 hover:bg-[#492A19] hover:text-[#F9DCAC] border-2"
                    style={{ borderColor: "#492A19" }}
                    onClick={() => setOpenForm("rondleiding")}
                  >
                    {t("home.registerTour")}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* About Us Section */}
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-12">
                {t("home.aboutUs.title")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div
                  className="p-8 rounded-2xl shadow-soft cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-300"
                  style={{ backgroundColor: "#FBEACD" }}
                  onClick={() => navigate("/about")}
                >
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    {t("home.aboutUs.welcomeCenter.title")}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t("home.aboutUs.welcomeCenter.description")}
                  </p>
                </div>
                <div
                  className="p-8 rounded-2xl shadow-soft cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-300"
                  style={{ backgroundColor: "#FBEACD" }}
                  onClick={() => navigate("/vision")}
                >
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    {t("home.aboutUs.growingImproving.title")}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t("home.aboutUs.growingImproving.description")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Our Services Section */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6">
                {t("home.services.title")}
              </h2>
              <p className="text-lg text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
                {t("home.services.subtitle")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-2xl shadow-soft cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-300"
                    style={{ backgroundColor: "#FBEACD" }}
                    onClick={() => navigate(service.path)}
                  >
                    <div className="text-4xl mb-4">{service.icon}</div>
                    <h3 className=" text-lg font-semibold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <ul className="text-left mx-auto my-auto text-gray-700 space-y-1 text-sm w-fit">
                      {Array.isArray(service.features)
                        ? service.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="text-center">
                              <span>{feature}</span>
                            </li>
                          ))
                        : null}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Forms Modals */}
          {openForm === "rondleiding" && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
              <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
                <button
                  onClick={() => setOpenForm(null)}
                  className="absolute top-4 right-4 text-2xl"
                >
                  &times;
                </button>
                <RondleidingForm />
              </div>
            </div>
          )}

          {/* WhatsApp Floating Contact Button */}
          <WhatsAppButton/>

          {/* Footer */}
          <footer>
            <Footer />
          </footer>
        </main>
      </div>
    </div>
  );
};

export default Home;
